'use client';

import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GetPriceRangeViewModel } from '@/types/api';
import { getPriceRangeURL } from '@/features/navigation/utils/url-helpers';
import { useDrawer } from '@/features/drawer/context/use-drawer';
import { useMobileDetect } from '@/features/mobile-detect-context/mobile-detect-provider';
import { getPriceRange } from '@/app/(plp)/_utils/get-price-range';
import { useBasicMetas } from '@/app/(plp)/_utils/use-basic-metas';
import { usePLPFacetsContext } from '@/app/(plp)/facets-context/facets-context';
import { PLPReactQueryKeys } from '@/app/(plp)/types';
import Input from './input';
import RangeSliderSkeleton from './skeleton';
import { type RangeArray, RangeObject } from './../types';
import { MobileFacetDrawerName } from '../constants';
interface Props {
  step?: number;
  sliderValue: RangeArray;
  defaultState: RangeObject;
  onStateChange?: (state: Partial<RangeObject>) => void;
}
const RangeSlider = ({
  step = 1,
  sliderValue,
  defaultState,
  onStateChange
}: Props) => {
  const {
    isMobile
  } = useMobileDetect();
  const {
    isDrawerOpen
  } = useDrawer();
  const isMobileFacetsOpen = isDrawerOpen(MobileFacetDrawerName);
  const {
    basicMetas
  } = useBasicMetas();
  const {
    page,
    pageFacets
  } = usePLPFacetsContext();
  const priceFacet = pageFacets?.priceFacet;
  const url = getPriceRangeURL(page, basicMetas);
  const rangeSliderEnabled = !!url && (!isMobile || isMobileFacetsOpen);
  const {
    isSuccess,
    isFetching,
    data
  } = useQuery<GetPriceRangeViewModel>({
    queryKey: [PLPReactQueryKeys.PriceRange, url],
    queryFn: () => getPriceRange(url),
    staleTime: Infinity,
    enabled: rangeSliderEnabled
  });
  const initialRange: RangeArray = useMemo(() => [data?.fromPrice || defaultState.min, data?.toPrice || defaultState.max], [data?.fromPrice, data?.toPrice, defaultState.max, defaultState.min]);
  const [range, setRange] = useState<RangeArray>(sliderValue);
  const updateValue = useCallback((newValue: RangeArray) => {
    if (!onStateChange) return;
    let updatedValue = {};
    if (newValue[0] !== initialRange[0]) updatedValue = {
      ...updatedValue,
      min: newValue[0]
    };
    if (newValue[1] !== initialRange[1]) updatedValue = {
      ...updatedValue,
      max: newValue[1]
    };
    onStateChange(updatedValue);
  }, [initialRange, onStateChange]);

  // Only call by slider - change internal value state AND if needed (userReleasedHandle === true) call parent change handler
  const handleChangeBySlider = useCallback((newRange: RangeArray, userReleasedHandle = false) => {
    setRange(newRange);
    userReleasedHandle && updateValue(newRange);
  }, [updateValue]);

  // Only call in inputs - change internal value state AND after call parent change handler
  const handleChangeByInput = (value: number, type: 'min' | 'max') => {
    if (isNaN(value) || type === 'max' && value === range[1] || type === 'min' && value === range[0]) return;
    const slideValue: RangeArray = type === 'min' ? [value, range[1]] : [range[0], value];
    setRange(slideValue);
    updateValue(slideValue);
  };

  // initialize range value after range api fetched data
  useEffect(() => {
    if (!isSuccess) return;
    const newValue = [];
    newValue.push(priceFacet?.selectedFromPrice || data.fromPrice);
    newValue.push(priceFacet?.selectedToPrice || data.toPrice);
    handleChangeBySlider(newValue as RangeArray);
  }, [isSuccess, priceFacet?.selectedFromPrice, priceFacet?.selectedToPrice, data?.fromPrice, data?.toPrice, handleChangeBySlider]);
  useEffect(() => {
    let newMin, newMax;

    // when priceFacet.selectedFromPrice changed
    if (!priceFacet?.selectedFromPrice) newMin = initialRange[0];else newMin = priceFacet?.selectedFromPrice;

    // when priceFacet.selectedToPrice changed
    if (!priceFacet?.selectedToPrice) newMax = initialRange[1];else newMax = priceFacet?.selectedToPrice;
    setRange([newMin, newMax]);
  }, [priceFacet?.selectedFromPrice, priceFacet?.selectedToPrice, initialRange]);
  if (isFetching) return <RangeSliderSkeleton />;
  return <div className='flex w-full flex-col gap-2 px-2 py-2.5 lg:px-4 lg:py-2' data-sentry-component="RangeSlider" data-sentry-source-file="range-slider.tsx">
            <div className='flex flex-col gap-4'>
                <Input initialValue={initialRange[0]} sliderValue={range[0]} onSliderChange={handleChangeByInput} type='min' step={step} data-sentry-element="Input" data-sentry-source-file="range-slider.tsx" />

                <Input initialValue={initialRange[1]} sliderValue={range[1]} onSliderChange={handleChangeByInput} type='max' step={step} data-sentry-element="Input" data-sentry-source-file="range-slider.tsx" />
            </div>

            <div className='mx-6 lg:mx-0'>
                <Slider range reverse={true} min={initialRange[0]} max={initialRange[1]} railStyle={{
        backgroundColor: '#FBCFE8'
      }} trackStyle={[{
        backgroundColor: '#DB2777'
      }]} step={step} className='mx-auto !h-2 !w-full !py-0.5 [&>.rc-slider-handle]:after:absolute [&>.rc-slider-handle]:after:right-1/2 [&>.rc-slider-handle]:after:top-1/2 [&>.rc-slider-handle]:after:block [&>.rc-slider-handle]:after:h-3 [&>.rc-slider-handle]:after:w-3 [&>.rc-slider-handle]:after:-translate-y-1/2 [&>.rc-slider-handle]:after:translate-x-1/2 [&>.rc-slider-handle]:after:rounded-full [&>.rc-slider-handle]:after:bg-primary-main' handleStyle={[{
        backgroundColor: 'transparent',
        opacity: 1,
        top: '-3px',
        border: 'none',
        width: '24px',
        height: '24px',
        boxShadow: 'none',
        touchAction: 'none',
        userSelect: 'none'
      }, {
        backgroundColor: 'transparent',
        opacity: 1,
        top: '-3px',
        border: 'none',
        width: '24px',
        height: '24px',
        boxShadow: 'none',
        touchAction: 'none',
        userSelect: 'none'
      }]} defaultValue={range} onChange={value => Array.isArray(value) ? handleChangeBySlider([value[0], value[1]]) : value}
      // TODO: check why is deprecated and if it's not ok find a better solution
      onAfterChange={value => Array.isArray(value) ? handleChangeBySlider([value[0], value[1]], true) : value} value={range} data-sentry-element="Slider" data-sentry-source-file="range-slider.tsx" />

                <div className='mt-1 flex items-center justify-between text-caption-12 text-text-darkGray'>
                    <span className='select-none'>ارزان‌ترین</span>
                    <span className='select-none'>گران‌ترین</span>
                </div>
            </div>
        </div>;
};
export default RangeSlider;